import CustomTooltip from '../../../shared/CustomTooltip';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { ModalProps } from '../../../types/modal';
import {
    Avatar,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import UserRole from '../../../../shared/AuthRoles';
import {
    useEditCompanyMutation,
    useGetIndividualOwnerAssetsQuery,
    useGetUserListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { MessagingUser } from '../../../types/user';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import { FormValues as Experience } from '../../../types/experience';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from '../../../shared/Toast';
import { Close, HelpOutline } from '@mui/icons-material';

const Schema = z.object({
    id: z.number().min(0, { message: 'Required' }),
    modules: z
        .array(z.number())
        .transform((arr) => arr.map((value) => ({ id: value }))),
});

interface UserModalProps extends ModalProps {
    messagingUsers: MessagingUser[];
    user: MessagingUser | null;
}

const UserModal = ({ open, onClose, messagingUsers, user }: UserModalProps) => {
    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            id: -1,
            modules: [],
        },
        resolver: zodResolver(Schema),
    });

    const loggedInUser = UserRole();

    const URL = `/owners/${loggedInUser?.company}`;

    const [experiencesOpen, setExperiencesOpen] = useState(false);

    // api
    const { data: companyUsers } = useGetUserListQuery(`${URL}/users`);
    const { data: experiences } = useGetIndividualOwnerAssetsQuery(
        {
            url: `${URL}/modules`,
            sort: 'name',
            status: 'published',
        },
        { skip: !loggedInUser?.role.includes('owner') },
    );
    const [updateCompany] = useEditCompanyMutation();

    const availableUsers =
        companyUsers?.filter(
            (companyUser: MessagingUser) =>
                !messagingUsers.some((user) => user.id === companyUser.id) ||
                companyUser.id === user?.id,
        ) || [];

    const { control, handleSubmit, reset, setValue } = methods;

    const onSubmit = async (data: FormValues) => {
        try {
            const existingUsers = messagingUsers.filter(
                (user) => user.id !== data.id,
            );

            const request = { messagingUsers: [...existingUsers, data] };

            await updateCompany([URL, request]).unwrap();

            toast.success('User updated successfully');
            handleClose();
        } catch (error) {
            toast.error('Error updating messaging users');
        }
    };

    const handleClose = () => {
        onClose();
        reset({ id: -1, modules: [] });
        setExperiencesOpen(false);
    };

    const modules = useWatch({
        name: 'modules',
        control,
    });

    const handleExperiences = () => {
        if (experiencesOpen) setValue('modules', []);

        setExperiencesOpen(!experiencesOpen);
    };

    useEffect(() => {
        if (modules?.length > 0) setExperiencesOpen(true);
    }, [modules]);

    useEffect(() => {
        if (user) {
            const { modules, ...rest } = user;

            reset({
                ...rest,
                // @ts-expect-error
                modules: modules.map(({ id }) => id),
            });
        }
    }, [user]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Assign User
                        <IconButton
                            onClick={handleClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <div>
                                <InputLabel required>User</InputLabel>
                                <FormAutocomplete<
                                    MessagingUser & {
                                        value: number;
                                        label: string;
                                    }
                                >
                                    name='id'
                                    control={control}
                                    disabled={!!user}
                                    options={
                                        availableUsers?.map(
                                            (user: MessagingUser) => ({
                                                value: user.id,
                                                label: `${user.firstName} ${user.lastName}`,
                                                ...user,
                                            }),
                                        ) || []
                                    }
                                    renderOption={(props, option, state) => (
                                        <li key={option.id} {...props}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={
                                                        option.profilePicture
                                                            ?.src
                                                    }
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`${option.firstName} ${option.lastName}`}
                                                secondary={option.email}
                                            />
                                        </li>
                                    )}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    label='Filter Experiences'
                                    control={<Switch />}
                                    checked={experiencesOpen}
                                    onChange={handleExperiences}
                                    sx={{ mr: 1 }}
                                />
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Filter which experiences the user
                                            should receive messages for. If
                                            unselected, the user will receive
                                            messages for all experiences.
                                        </Typography>
                                    }
                                >
                                    <HelpOutline
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                                {experiencesOpen && (
                                    <>
                                        <FormMultiAutocomplete
                                            name='modules'
                                            control={control}
                                            options={
                                                experiences?.map(
                                                    (
                                                        experience: Experience,
                                                    ) => ({
                                                        value: experience.id,
                                                        label: experience.name,
                                                        ...experience,
                                                    }),
                                                ) || []
                                            }
                                            renderOption={(
                                                props,
                                                option: Experience,
                                            ) => (
                                                <li key={option.id} {...props}>
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            variant='rounded'
                                                            src={
                                                                option
                                                                    .photos?.[0]
                                                                    ?.src
                                                            }
                                                        />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={option.name}
                                                        secondary={`${option.streetAddress}, ${option.suburb}`}
                                                    />
                                                </li>
                                            )}
                                        />
                                        <FormHelperText>
                                            Receive notifications relevant to
                                            selected experiences only
                                        </FormHelperText>
                                    </>
                                )}
                            </div>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button type='submit' variant='contained'>
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default UserModal;
